import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCheck, 
    faArrowLeft, 
    faShieldAlt,
    faRocket,
    faCrown,
    faCircleCheck,
    faStar,
    faBolt,
    faGem,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import Sidebar from '../Sidebar/Sidebar';
import { loadStripe } from '@stripe/stripe-js';
import { buildApiUrl } from '../../config/api.config';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_CLIENT_KEY);

const featureDescriptions = {
    'Inventory Management': { title: 'Stock & Raw Materials', desc: 'Track ingredients (kg/liters) so you know when to buy more.' },
    'Invoicing & GST Billing': { title: 'GST Invoices & Reports', desc: 'Generate professional GST-compliant invoices and tax filings.' },
    'Kitchen Display (KDS)': { title: 'Live Kitchen Screen', desc: 'Digital screen for chefs to see orders instantly (no paper tickets).' },
    'Table Reservation': { title: 'Table Booking Manager', desc: 'Manage upcoming reservations and real-time table availability.' },
    'Point of Sale (POS)': { title: 'Smart Billing Terminal', desc: 'Fast billing interface for orders, payments, and receipt printing.' },
    'Staff Management': { title: 'Team Roles & Logins', desc: 'Control distinct access levels for waiters, chefs, and managers.' },
    'Advanced Analytics': { title: 'Profit & Sales Reports', desc: 'Visual graphs showing best-selling items and total revenue trends.' },
    'Customer Database': { title: 'Customer History', desc: 'Track regular customers, their order history, and preferences.' },
    'Review Management': { title: 'Ratings & Feedback', desc: 'Collect and reply to customer reviews to build reputation.' },
    'Attendance & Payroll': { title: 'Attendance & Salary', desc: 'Automated shift tracking and monthly salary calculation.' },
    'Advanced Security': { title: 'Data Security Shield', desc: 'Enterprise-grade encryption to protect your sensitive business data.' }
};

const PlanSelection = () => {
    const navigate = useNavigate();
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [processingPlanId, setProcessingPlanId] = useState(null);
    const [restaurantData, setRestaurantData] = useState(null);
    const [hoveredFeature, setHoveredFeature] = useState(null);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await axios.get(buildApiUrl("subscription-plans"));
                if (response.data.success) {
                    setSubscriptionPlans(response.data.plans);
                }
            } catch (error) {
                console.error("Error fetching plans:", error);
            }
        };

        const fetchRestData = async () => {
             const user = JSON.parse(localStorage.getItem("userDetails"));
             const selectedRestId = localStorage.getItem("currentRestaurantId");
             const isMaster = user?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
             let activeRestaurantId = isMaster ? selectedRestId : user?.restaurantId;

             if (activeRestaurantId && typeof activeRestaurantId === 'object') {
                 activeRestaurantId = activeRestaurantId._id;
             }
             
             if (activeRestaurantId) {
                 const response = await axios.get(buildApiUrl(`restaurant/${activeRestaurantId}`));
                 if (response.data.success) {
                     setRestaurantData(response.data.restaurant);
                 }
             }
        };

        fetchPlans();
        fetchRestData();
    }, []);

    const handleSelectPlan = async (planId) => {
        try {
            setProcessingPlanId(planId);
            const user = JSON.parse(localStorage.getItem("userDetails"));
            const token = localStorage.getItem("token");
            const selectedRestId = localStorage.getItem("currentRestaurantId");
            const isMaster = user?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
            let activeRestaurantId = isMaster ? selectedRestId : user?.restaurantId;

            if (activeRestaurantId && typeof activeRestaurantId === 'object') {
                activeRestaurantId = activeRestaurantId._id;
            }

            const response = await axios.post(buildApiUrl("stripe/create-checkout-session"), {
                planType: planId, // Backend expects planType
                restaurantId: activeRestaurantId,
                userId: user._id,
                billingCycle: billingCycle
            }, {
                headers: {
                    'auth-token': token
                }
            });

            if (response.data.sessionId) {
                const stripe = await stripePromise;
                const { error } = await stripe.redirectToCheckout({
                    sessionId: response.data.sessionId,
                });

                if (error) {
                    console.error("Stripe Redirect Error:", error);
                    setProcessingPlanId(null);
                }
            } else if (response.data.url) {
                // Some versions of the controller return url directly
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error("Error initiating payment:", error);
            setProcessingPlanId(null);
        }
    };

    const getPlanIcon = (planName) => {
        const name = planName.toLowerCase();
        if (name.includes('basic')) return faBolt;
        if (name.includes('premium') || name.includes('pro')) return faRocket;
        return faGem;
    };

    const [billingCycle, setBillingCycle] = useState('monthly');

    return (
        <div className="admin-layout plan-selection-container">
            <Sidebar />
            
            <main className="plan-selection-main">
                <div className="plan-content-wrapper">
                    
                    {/* Header Section */}
                    <div className="plan-header-top">
                        <button className="back-button" onClick={() => navigate('/admin/settings')}>
                            <FontAwesomeIcon icon={faArrowLeft} /> Back
                        </button>
                    </div>

                    <div className="plan-header-content">
                        <div className="plan-title-section">
                            <h1 className="plan-title">Subscription Plans</h1>
                            <p className="plan-subtitle">Choose the perfect plan for your needs. Upgrade or downgrade anytime.</p>
                        </div>
                        
                        <div className="plan-toggle-section">
                            <div className="discount-tag">
                                <FontAwesomeIcon icon={faCheck} style={{ marginRight: '6px' }}/>
                                Save up to 20% with annual billing
                            </div>
                            <div className="billing-toggle">
                                <button 
                                    className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                                    onClick={() => setBillingCycle('monthly')}
                                >Monthly</button>
                                <button 
                                    className={`toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                                    onClick={() => setBillingCycle('yearly')}
                                >Yearly <span className="save-badge">&larr; Save 20%</span></button>
                            </div>
                        </div>
                    </div>

                    {/* Plans Grid */}
                    <div className="plans-grid">
                        {subscriptionPlans.map((plan, planIdx) => {
                            const isRecommended = plan.bestValue;
                            const isEnterprise = plan.name.toLowerCase().includes('enterprise') || plan.name.toLowerCase().includes('custom');
                            
                            return (
                                <div key={plan.planId} className={`plan-card ${isRecommended ? 'recommended' : ''}`}>
                                    {isRecommended && (
                                        <div className="recommended-badge">
                                            Most Popular
                                        </div>
                                    )}

                                    {/* Plan Info */}
                                    <div className="plan-info">
                                        <div className="plan-icon-wrapper">
                                            <FontAwesomeIcon icon={getPlanIcon(plan.name)} />
                                        </div>
                                        <h2 className="plan-name">{plan.name}</h2>
                                        <p className="plan-desc">Perfect for getting started with the basics.</p>
                                        
                                        <div className="plan-price-wrapper">
                                            <span className="plan-currency">₹</span>
                                            <span className="plan-price">{billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}</span>
                                            <span className="plan-duration">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                                        </div>
                                    </div>

                                    {/* Features List */}
                                    <div className="plan-features">
                                        <div className="features-list">
                                            {plan.features?.map((feature, i) => (
                                                <div key={i} className="feature-item">
                                                    <FontAwesomeIcon icon={faCheck} className="feature-check" />
                                                    <span className="feature-text">{featureDescriptions[feature]?.title || feature}</span>
                                                </div>
                                            ))}
                                            <div className="feature-item">
                                                <FontAwesomeIcon icon={faCheck} className="feature-check" />
                                                <span className="feature-text">{billingCycle === 'yearly' ? '365' : '30'} Days Node Validation</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* CTA Button */}
                                    <button 
                                        className={`plan-cta-button ${isRecommended ? 'btn-solid' : 'btn-outline'} ${processingPlanId === plan.planId ? 'processing' : ''}`}
                                        onClick={() => handleSelectPlan(plan.planId)}
                                        disabled={processingPlanId !== null}
                                    >
                                        {processingPlanId === plan.planId ? 'Processing...' : (plan.planId === restaurantData?.currentPlan ? 'Current Plan' : (isEnterprise ? 'Contact Sales' : 'Choose Plan'))}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* Footer Guarantee */}
                    <div className="plan-footer-guarantee">
                        <div className="guarantee-icon">
                            <FontAwesomeIcon icon={faShieldAlt} />
                        </div>
                        <div className="guarantee-text">
                            <strong>30-Day Money-Back Guarantee</strong>
                            <span>Not satisfied? Get a full refund within 30 days of your purchase.</span>
                        </div>
                        <div className="guarantee-link">
                            Learn more <FontAwesomeIcon icon={faArrowLeft} style={{ transform: 'rotate(180deg)' }}/>
                        </div>
                    </div>
                </div>
            </main>

            <style>{`
                /* Dark Theme Styling matching existing app */
                .plan-selection-container {
                    display: flex;
                    height: 100vh;
                    background: #0a0a0c;
                    color: #ffffff;
                    overflow: hidden;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                }
                
                .plan-selection-main {
                    flex: 1;
                    height: 100vh;
                    overflow: hidden;
                    padding: 1.5rem 2rem;
                    background: radial-gradient(circle at 50% 0%, rgba(30, 32, 45, 0.8) 0%, rgba(10, 10, 12, 1) 70%);
                    display: flex;
                    flex-direction: column;
                }

                .plan-content-wrapper {
                    max-width: 1200px;
                    margin: 0 auto;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }

                /* Header Layout */
                .plan-header-top {
                    margin-bottom: 0.5rem;
                }

                .back-button {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: white;
                    padding: 0.4rem 0.8rem;
                    border-radius: 8px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.85rem;
                    font-weight: 600;
                    transition: all 0.2s ease;
                    backdrop-filter: blur(5px);
                }
                .back-button:hover {
                    background: rgba(255, 255, 255, 0.1);
                    transform: translateY(-2px);
                }

                .plan-header-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 1.5rem;
                    flex-shrink: 0;
                }

                .plan-title-section {
                    max-width: 600px;
                }

                .plan-title {
                    font-size: 1.8rem;
                    font-weight: 800;
                    margin: 0 0 0.4rem 0;
                    background: linear-gradient(135deg, #ffffff 0%, #a0a5b1 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    letter-spacing: -0.5px;
                }

                .plan-subtitle {
                    color: #8a8f9a;
                    font-size: 1rem;
                    margin: 0;
                    line-height: 1.4;
                }

                /* Toggle Section */
                .plan-toggle-section {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 0.5rem;
                }

                .discount-tag {
                    display: inline-flex;
                    align-items: center;
                    background: rgba(255, 255, 255, 0.05);
                    color: var(--tf-primary, #4facfe);
                    padding: 0.3rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    border: 1px solid rgba(255, 255, 255, 0.05);
                }

                .billing-toggle {
                    display: flex;
                    background: rgba(255, 255, 255, 0.05);
                    padding: 0.2rem;
                    border-radius: 8px;
                    border: 1px solid rgba(255, 255, 255, 0.05);
                }

                .toggle-btn {
                    background: transparent;
                    border: none;
                    padding: 0.4rem 1.2rem;
                    border-radius: 6px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #8a8f9a;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    transition: all 0.2s ease;
                }

                .toggle-btn.active {
                    background: rgba(255, 255, 255, 0.1);
                    color: white;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                }

                .save-badge {
                    color: #000;
                    background: var(--tf-primary, #4facfe);
                    padding: 0.1rem 0.4rem;
                    border-radius: 4px;
                    font-size: 0.65rem;
                }

                /* Grid */
                .plans-grid {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    gap: 1.5rem;
                    align-items: stretch;
                    justify-content: center;
                    flex: 1;
                    min-height: 0;
                }

                /* Cards */
                .plan-card {
                    flex: 1 1 0;
                    min-width: 0;
                    max-width: 360px;
                    background: rgba(20, 21, 26, 0.6);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 12px;
                    padding: 1.5rem;
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
                    backdrop-filter: blur(20px);
                    transition: all 0.3s ease;
                }
                
                .plan-card:hover {
                    transform: translateY(-4px);
                    border-color: rgba(255, 255, 255, 0.15);
                }

                .plan-card.recommended {
                    background: linear-gradient(180deg, rgba(25, 27, 36, 0.95) 0%, rgba(20, 21, 26, 0.85) 100%);
                    border-color: var(--tf-primary, #4facfe);
                    box-shadow: 0 0 0 1px var(--tf-primary, #4facfe), 0 15px 35px rgba(0, 0, 0, 0.25);
                }

                .recommended-badge {
                    position: absolute;
                    top: -10px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
                    color: white;
                    padding: 0.2rem 0.8rem;
                    border-radius: 4px;
                    font-size: 0.7rem;
                    font-weight: 700;
                    white-space: nowrap;
                    box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4);
                }

                /* Info */
                .plan-info {
                    margin-bottom: 1rem;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                    padding-bottom: 1rem;
                    flex-shrink: 0;
                    text-align: left;
                }

                .plan-icon-wrapper {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: rgba(255, 255, 255, 0.04);
                    color: var(--tf-primary, #4facfe);
                    font-size: 1.1rem;
                    margin-bottom: 0.8rem;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                }
                
                .plan-card.recommended .plan-icon-wrapper {
                    background: linear-gradient(135deg, rgba(79, 172, 254, 0.12) 0%, rgba(0, 242, 254, 0.12) 100%);
                    border-color: rgba(79, 172, 254, 0.3);
                }

                .plan-name {
                    font-size: 1.25rem;
                    font-weight: 800;
                    margin: 0 0 0.3rem 0;
                    color: white;
                }
                
                .plan-desc {
                    color: #8a8f9a;
                    font-size: 0.85rem;
                    margin: 0 0 1rem 0;
                    line-height: 1.4;
                }

                .plan-price-wrapper {
                    display: flex;
                    align-items: baseline;
                    gap: 0.2rem;
                }
                
                .plan-currency {
                    font-size: 1.2rem;
                    font-weight: 700;
                    color: #8a8f9a;
                }

                .plan-price {
                    font-size: 2.2rem;
                    font-weight: 800;
                    color: white;
                    letter-spacing: -0.5px;
                }

                .plan-duration {
                    color: #8a8f9a;
                    font-weight: 500;
                    font-size: 0.9rem;
                }

                /* Features */
                .plan-features {
                    flex: 1;
                    margin-bottom: 1rem;
                    overflow-y: auto;
                    padding-right: 5px;
                }
                
                /* Custom scrollbar */
                .plan-features::-webkit-scrollbar {
                    width: 4px;
                }
                .plan-features::-webkit-scrollbar-track {
                    background: transparent;
                }
                .plan-features::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 4px;
                }

                .features-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.8rem;
                }

                .feature-item {
                    display: flex;
                    align-items: flex-start;
                }
                
                .feature-check {
                    color: var(--tf-primary, #4facfe);
                    font-size: 0.8rem;
                    margin-right: 12px;
                    margin-top: 4px;
                }

                .feature-text {
                    color: #d1d5db;
                    font-size: 0.85rem;
                    line-height: 1.4;
                }

                /* CTA */
                .plan-cta-button {
                    width: 100%;
                    padding: 0.6rem;
                    border-radius: 6px;
                    font-weight: 700;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    flex-shrink: 0;
                    margin-top: auto;
                }
                
                .btn-solid {
                    background: var(--tf-primary, linear-gradient(90deg, #4facfe 0%, #00f2fe 100%));
                    border: none;
                    color: white;
                    box-shadow: 0 4px 15px rgba(79, 172, 254, 0.3);
                }
                .btn-solid:hover {
                    box-shadow: 0 6px 20px rgba(79, 172, 254, 0.5);
                    filter: brightness(1.1);
                }
                
                .btn-outline {
                    background: rgba(255, 255, 255, 0.05);
                    color: white;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                .btn-outline:hover {
                    background: rgba(255, 255, 255, 0.1);
                }

                .plan-cta-button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                /* Footer Guarantee */
                .plan-footer-guarantee {
                    margin-top: 1rem;
                    background: rgba(20, 21, 26, 0.6);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 8px;
                    padding: 1rem 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 1.2rem;
                    flex-shrink: 0;
                }
                
                .guarantee-icon {
                    background: rgba(255, 255, 255, 0.05);
                    color: var(--tf-primary, #4facfe);
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1rem;
                }
                
                .guarantee-text {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 0.1rem;
                }
                
                .guarantee-text strong {
                    color: white;
                    font-size: 0.9rem;
                }
                
                .guarantee-text span {
                    color: #8a8f9a;
                    font-size: 0.85rem;
                }
                
                .guarantee-link {
                    color: var(--tf-primary, #4facfe);
                    font-weight: 600;
                    font-size: 0.85rem;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                }

                /* Responsive */
                @media (max-width: 1024px) {
                    .plan-header-content {
                        flex-direction: column;
                        gap: 1.5rem;
                        margin-bottom: 1rem;
                    }
                    .plan-toggle-section {
                        align-items: flex-start;
                    }
                }
            `}</style>
        </div>
    );
};

export default PlanSelection;
